import React from "react";
import { Thermometer, Wrench, Home as HomeIcon, Flame, Sun, Zap, DoorOpen, Droplets } from "lucide-react";

/**
 * HomeServicesGlobe — "solar system" visualization.
 * A realistic latitude/longitude globe (the sun) with colored planet spheres
 * (the offers) slowly revolving on dashed orbit rings, over a star field.
 * Pure CSS animations, self-contained — nothing leaks into global CSS.
 */

// Each planet: service icon, label, payout, orbit start angle, and its own
// sphere colors (light highlight → base) + glow.
const PLANETS = [
  { icon: Droplets,    label: "Water & Fire",  payout: "$515", angle: 0,   c1: "#fb923c", c2: "#dc2626", glow: "rgba(249,115,22,0.5)" },
  { icon: HomeIcon,    label: "Roofers",       payout: "$178", angle: 45,  c1: "#2dd4bf", c2: "#0f766e", glow: "rgba(45,212,191,0.45)" },
  { icon: Wrench,      label: "Plumbers",      payout: "$98",  angle: 90,  c1: "#3b82f6", c2: "#1e3a8a", glow: "rgba(59,130,246,0.5)" },
  { icon: Flame,       label: "Heating",       payout: "$96",  angle: 135, c1: "#fb923c", c2: "#c2410c", glow: "rgba(234,88,12,0.5)" },
  { icon: Thermometer, label: "HVAC",          payout: "$94",  angle: 180, c1: "#bae6fd", c2: "#38bdf8", glow: "rgba(125,211,252,0.5)" },
  { icon: Zap,         label: "Electricians",  payout: "$57",  angle: 225, c1: "#fde047", c2: "#d97706", glow: "rgba(251,191,36,0.5)" },
  { icon: Sun,         label: "Solar",         payout: "$53",  angle: 270, c1: "#fef08a", c2: "#facc15", glow: "rgba(250,204,21,0.55)" },
  { icon: DoorOpen,    label: "Garage Doors",  payout: "$77",  angle: 315, c1: "#e2e8f0", c2: "#94a3b8", glow: "rgba(148,163,184,0.45)" },
];

// Deterministic star field (no Math.random → stable SSR/renders).
const STARS = Array.from({ length: 42 }, (_, i) => ({
  top: `${(i * 37 + 11) % 100}%`,
  left: `${(i * 53 + 7) % 100}%`,
  size: i % 5 === 0 ? 2 : 1,
  delay: `${(i % 9) * 0.45}s`,
}));

const HomeServicesGlobe = () => (
  <div
    className="hs-globe-scene relative aspect-square w-full max-w-[560px] mx-auto"
    aria-hidden="true"
    data-testid="home-services-globe"
  >
    {/* Star field */}
    {STARS.map((s, i) => (
      <span
        key={i}
        className="hs-star"
        style={{ top: s.top, left: s.left, width: s.size, height: s.size, animationDelay: s.delay }}
      />
    ))}

    {/* Soft outer glow behind the globe */}
    <div className="hs-globe-glow" />

    {/* Dashed orbit rings — slow rotation */}
    <div className="hs-orbit hs-orbit-1">
      <span className="hs-orbit-dot hs-orbit-dot-blue" />
    </div>
    <div className="hs-orbit hs-orbit-2">
      <span className="hs-orbit-dot hs-orbit-dot-cyan" />
    </div>

    {/* The globe — rotating Earth map texture on a shaded sphere */}
    <div className="hs-globe">
      <div className="hs-map" />
      {/* Spherical shading: night side + top-left light */}
      <div className="hs-sphere-shade" />
      <div className="hs-highlight" />
    </div>

    {/* Orbiting planets — wrapper revolves; planet unit counter-rotates so
        the sphere + caption stay upright. */}
    {PLANETS.map((o, i) => {
      const delay = `${-i * (26 / PLANETS.length)}s`;
      return (
        <div key={o.label} className="hs-orbit-wrap" style={{ animationDelay: delay }}>
          <div className="hs-planet-slot">
            <div className="hs-planet-unit" style={{ animationDelay: delay }}>
              <div
                className="hs-planet"
                style={{
                  background: `radial-gradient(circle at 32% 28%, ${o.c1} 0%, ${o.c2} 62%, #0b1220 135%)`,
                  boxShadow: `inset -6px -8px 14px rgba(2,6,23,0.55), inset 4px 5px 10px rgba(255,255,255,0.22), 0 0 18px ${o.glow}`,
                }}
              >
                <div className="hs-planet-texture" />
                <o.icon className="w-4 h-4 text-white relative z-10 drop-shadow" strokeWidth={2.25} />
              </div>
              <div className="hs-planet-caption">
                <span className="hs-planet-name">{o.label}</span>
                <span className="hs-planet-price">Up to {o.payout}</span>
              </div>
            </div>
          </div>
        </div>
      );
    })}

    {/* Central label — over the globe */}
    <div className="hs-center-label">
      <span className="hs-center-eyebrow">USA · Live network</span>
      <span className="hs-center-title">100+ Offers</span>
      <span className="hs-center-sub">across all 50 states</span>
    </div>

    <style>{`
      /* Scene ------------------------------------------------------------ */
      .hs-globe-scene {
        --globe-size: 58%;
        perspective: 1200px;
      }

      /* Stars ------------------------------------------------------------ */
      .hs-star {
        position: absolute;
        background: #e2e8f0;
        border-radius: 9999px;
        opacity: 0.45;
        animation: hs-twinkle 3.2s ease-in-out infinite;
        pointer-events: none;
      }

      .hs-globe-glow {
        position: absolute;
        inset: 8%;
        border-radius: 9999px;
        background: radial-gradient(circle at 50% 50%,
          rgba(59, 130, 246, 0.4) 0%,
          rgba(147, 51, 234, 0.18) 42%,
          transparent 72%);
        filter: blur(34px);
        pointer-events: none;
      }

      /* Dashed orbit rings ----------------------------------------------- */
      .hs-orbit {
        position: absolute;
        inset: 8%;
        border-radius: 9999px;
        border: 1px dashed rgba(148, 163, 184, 0.3);
      }
      .hs-orbit-1 {
        animation: hs-spin-cw 34s linear infinite;
      }
      .hs-orbit-2 {
        inset: 2%;
        border-style: dotted;
        border-color: rgba(56, 189, 248, 0.3);
        animation: hs-spin-ccw 46s linear infinite;
      }
      .hs-orbit-dot {
        position: absolute;
        top: -4px;
        left: 50%;
        width: 8px;
        height: 8px;
        border-radius: 9999px;
        transform: translateX(-50%);
      }
      .hs-orbit-dot-blue {
        background: #3b82f6;
        box-shadow: 0 0 12px rgba(59,130,246,0.9);
      }
      .hs-orbit-dot-cyan {
        background: #22d3ee;
        box-shadow: 0 0 12px rgba(34,211,238,0.9);
      }

      /* The globe — Earth texture panning across a shaded sphere ---------- */
      .hs-globe {
        position: absolute;
        top: 50%;
        left: 50%;
        width: var(--globe-size);
        aspect-ratio: 1 / 1;
        transform: translate(-50%, -50%);
        border-radius: 9999px;
        background: #12275f;
        box-shadow:
          inset -18px -14px 50px rgba(2, 6, 23, 0.75),
          inset 14px 12px 45px rgba(59, 130, 246, 0.25),
          0 0 55px rgba(59, 130, 246, 0.35),
          0 30px 60px rgba(30, 64, 175, 0.35);
        overflow: hidden;
        animation: hs-globe-tilt 12s ease-in-out infinite;
      }
      /* The panning world map — two tile widths wide, slides left forever */
      .hs-map {
        position: absolute;
        top: 0;
        left: 0;
        width: 200%;
        height: 100%;
        background-image: url('/earth-map.jpg');
        background-repeat: repeat-x;
        background-size: auto 100%;
        animation: hs-earth-rotate 26s linear infinite;
        will-change: transform;
      }
      /* Night-side shading so the flat map reads as a sphere */
      .hs-sphere-shade {
        position: absolute;
        inset: 0;
        border-radius: 9999px;
        background: radial-gradient(circle at 32% 30%,
          transparent 0%,
          rgba(2, 6, 23, 0.08) 42%,
          rgba(2, 6, 23, 0.55) 78%,
          rgba(2, 6, 23, 0.88) 100%);
        pointer-events: none;
      }

      /* Faux 3D highlight (top-left) */
      .hs-highlight {
        position: absolute;
        top: 6%;
        left: 12%;
        width: 55%;
        height: 55%;
        border-radius: 9999px;
        background: radial-gradient(circle at 30% 30%,
          rgba(255, 255, 255, 0.3), transparent 60%);
        pointer-events: none;
      }

      /* Central label ----------------------------------------------------- */
      .hs-center-label {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
        z-index: 6;
        pointer-events: none;
      }
      .hs-center-eyebrow {
        font-size: 10px;
        letter-spacing: 0.22em;
        font-weight: 700;
        text-transform: uppercase;
        color: #67e8f9;
        margin-bottom: 8px;
      }
      .hs-center-title {
        font-family: 'Poppins', Inter, sans-serif;
        font-weight: 700;
        font-size: clamp(28px, 4.5vw, 44px);
        color: #ffffff;
        line-height: 1;
        letter-spacing: -0.02em;
        text-shadow: 0 4px 20px rgba(0, 0, 0, 0.45);
      }
      .hs-center-sub {
        margin-top: 6px;
        color: rgba(226, 232, 240, 0.9);
        font-size: 12px;
        font-weight: 500;
      }

      /* Orbiting planets -------------------------------------------------- */
      .hs-orbit-wrap {
        position: absolute;
        inset: 0;
        animation: hs-spin-cw 26s linear infinite;
        will-change: transform;
      }
      .hs-planet-slot {
        position: absolute;
        top: 3%;
        left: 50%;
        transform: translateX(-50%);
      }
      /* Counter-rotate so planet + caption stay upright while orbiting */
      .hs-planet-unit {
        animation: hs-spin-ccw 26s linear infinite;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 7px;
      }
      .hs-planet {
        position: relative;
        width: 46px;
        height: 46px;
        border-radius: 9999px;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
        flex-shrink: 0;
      }
      /* Subtle surface texture */
      .hs-planet-texture {
        position: absolute;
        inset: 0;
        border-radius: 9999px;
        background-image: radial-gradient(rgba(255, 255, 255, 0.28) 1px, transparent 1.6px);
        background-size: 9px 9px;
        opacity: 0.32;
        mix-blend-mode: overlay;
        pointer-events: none;
      }
      .hs-planet-caption {
        text-align: center;
        line-height: 1.15;
        white-space: nowrap;
      }
      .hs-planet-name {
        display: block;
        font-size: 11px;
        font-weight: 700;
        color: #ffffff;
        letter-spacing: 0.01em;
        text-shadow: 0 2px 10px rgba(2, 6, 23, 0.85);
      }
      .hs-planet-price {
        display: block;
        margin-top: 2px;
        font-size: 10px;
        font-weight: 600;
        color: #e2e8f0;
        text-shadow: 0 2px 10px rgba(2, 6, 23, 0.85);
      }

      /* Keyframes --------------------------------------------------------- */
      @keyframes hs-spin-cw { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
      @keyframes hs-spin-ccw { from { transform: rotate(0deg); } to { transform: rotate(-360deg); } }
      @keyframes hs-earth-rotate { from { transform: translateX(0); } to { transform: translateX(-50%); } }
      @keyframes hs-twinkle {
        0%, 100% { opacity: 0.15; }
        50%      { opacity: 0.7; }
      }
      @keyframes hs-globe-tilt {
        0%, 100% { transform: translate(-50%, -50%) rotate(-1.5deg); }
        50%      { transform: translate(-50%, -50%) rotate(1.5deg); }
      }

      /* Respect prefers-reduced-motion */
      @media (prefers-reduced-motion: reduce) {
        .hs-orbit, .hs-orbit-1, .hs-orbit-2,
        .hs-star, .hs-map,
        .hs-orbit-wrap, .hs-planet-unit, .hs-globe {
          animation: none !important;
        }
      }

      /* Mobile tuning ------------------------------------------------------ */
      @media (max-width: 640px) {
        .hs-planet { width: 36px; height: 36px; }
        .hs-planet-name { font-size: 10px; }
        .hs-planet-price { font-size: 9px; }
        .hs-planet-slot { top: 2%; }
      }
    `}</style>
  </div>
);

export default HomeServicesGlobe;
